import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import '../../Styles/ManagementPages.css';
import '../../Styles/CustomerManage.css';
import {
  getEmployees,
  getEmployeeById,
  createEmployee,
  updateEmployee,
  deleteEmployee,
  permanentDeleteEmployee,
  updateEmployeePassword,
  bulkCreateEmployees,
  approveUser,
  rejectUser,
  togglePayrollStatus,
} from '../../Services/Operations/ProductAPI';

const SkeletonStatsCard = () => (
  <div className="stat-card skeleton">
    <div className="skeleton-title"></div>
    <div className="skeleton-value"></div>
    <div className="skeleton-subtitle"></div>
  </div>
);

const SkeletonTableRow = () => (
  <tr className="skeleton-row">
    <td><div className="skeleton-cell"></div></td>
    <td><div className="skeleton-cell"></div></td>
    <td><div className="skeleton-cell"></div></td>
    <td><div className="skeleton-cell"></div></td>
    <td><div className="skeleton-cell"></div></td>
    <td><div className="skeleton-cell"></div></td>
    <td><div className="skeleton-cell"></div></td>
    <td><div className="skeleton-cell"></div></td>
    <td><div className="skeleton-badge"></div></td>
    <td><div className="skeleton-actions"></div></td>
  </tr>
);

const SkeletonTable = () => (
  <div className="table-container skeleton">
    <table className="data-table">
      <thead>
        <tr>
          <th>No.</th>
          <th>Name</th>
          <th>Email</th>
          <th>Mobile</th>
          <th>Type</th>
          <th>Role</th>
          <th>Department</th>
          <th>Position</th>
          <th>Status</th>
          <th>Actions</th>
        </tr>
      </thead>
      <tbody>
        {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((i) => (
          <SkeletonTableRow key={i} />
        ))}
      </tbody>
    </table>
  </div>
);

const SkeletonModal = () => (
  <div className="modal-overlay">
    <div className="modal-content1 skeleton-modal">
      <div className="modal-header">
        <div className="skeleton-title"></div>
        <div className="skeleton-close"></div>
      </div>
      <div className="modal-body">
        <div className="skeleton-profile-image"></div>
        {[1, 2, 3, 4, 5, 6, 7].map((i) => (
          <div key={i} className="skeleton-detail-row">
            <div className="skeleton-label"></div>
            <div className="skeleton-value"></div>
          </div>
        ))}
      </div>
      <div className="modal-footer">
        <div className="skeleton-button"></div>
      </div>
    </div>
  </div>
);

const EmployeeManagement = () => {
  const { token } = useSelector(state => state.user);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searching, setSearching] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [viewingEmployee, setViewingEmployee] = useState(null);
  const [error, setError] = useState(null);
  const [isFetchingDetails, setIsFetchingDetails] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [addForm, setAddForm] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    type: 'employee',
    role: 'employee',
    phoneNumber: '',
    department: '',
    position: '',
    payroll: {
      employeeId: '',
      designation: '',
      dateOfJoining: '',
      salary: {
        basic: 0,
        hra: 0,
        allowances: 0,
        deductions: 0,
        netSalary: 0,
        currency: 'INR'
      },
      bankDetails: {
        accountHolderName: '',
        accountNumber: '',
        ifscCode: '',
        bankName: '',
        branch: ''
      },
      taxDetails: {
        panNumber: '',
        pfNumber: '',
        esiNumber: ''
      },
      paymentMode: 'bank_transfer',
      paymentCycle: 'monthly',
      status: 'inactive',
      lastPaidOn: '',
      remarks: ''
    }
  });
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    newPassword: '',
    confirmPassword: ''
  });
  const [selectedEmployeeId, setSelectedEmployeeId] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteType, setDeleteType] = useState('soft');
  const [employeeToDelete, setEmployeeToDelete] = useState(null);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [bulkEmployees, setBulkEmployees] = useState('');
  const [filters, setFilters] = useState({
    type: '',
    role: '',
    department: '',
    status: ''
  });
  const [isAddingEmployee, setIsAddingEmployee] = useState(false);
  const [isUpdatingPassword, setIsUpdatingPassword] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  
  const [payrollToggling, setPayrollToggling] = useState({});
  
  const [showEditModal, setShowEditModal] = useState(false);
  const [editForm, setEditForm] = useState({
    name: '',
    email: '',
    phoneNumber: '',
    type: 'employee',
    role: 'employee',
    department: '',
    position: '',
    isActive: true,
    payroll: {
      employeeId: '',
      designation: '',
      dateOfJoining: '',
      salary: {
        basic: 0,
        hra: 0,
        allowances: 0,
        deductions: 0,
        netSalary: 0,
        currency: 'INR'
      },
      bankDetails: {
        accountHolderName: '',
        accountNumber: '',
        ifscCode: '',
        bankName: '',
        branch: ''
      },
      taxDetails: {
        panNumber: '',
        pfNumber: '',
        esiNumber: ''
      },
      paymentMode: 'bank_transfer',
      paymentCycle: 'monthly',
      status: 'inactive',
      lastPaidOn: '',
      remarks: ''
    }
  });
  const [editingEmployeeId, setEditingEmployeeId] = useState(null);
  const [isSavingEdit, setIsSavingEdit] = useState(false);

  const [validationErrors, setValidationErrors] = useState({});

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalEmployees, setTotalEmployees] = useState(0);
  const [pageSize] = useState(10);

  const roleOptions = ['employee', 'advanced employee'];
  const typeOptions = ['employee', 'executive'];
  const departmentOptions = ['Operations', 'IT', 'HR', 'Finance', 'Sales', 'Marketing', 'Customer Support'];
  const paymentModeOptions = ['bank_transfer', 'cash', 'cheque', 'upi'];
  const paymentCycleOptions = ['monthly', 'weekly', 'biweekly'];
  const payrollStatusOptions = ['active', 'inactive', 'on_hold'];
  const currencyOptions = ['INR', 'USD', 'EUR', 'GBP', 'AUD', 'CAD', 'JPY', 'CNY'];

  const getSequentialNumber = (index) => {
    return (currentPage - 1) * pageSize + index + 1;
  };

  const validateEmail = (email) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  };

  const validatePhone = (phone) => {
    const re = /^(\+\d{1,3}[\s-]?)?\(?\d{3}\)?[\s-]?\d{3}[\s-]?\d{4}$/;
    return re.test(phone) || phone === '';
  };

  const validatePAN = (pan) => {
    const re = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;
    return re.test(pan) || pan === '';
  };

  const validateIFSC = (ifsc) => {
    const re = /^[A-Z]{4}0[A-Z0-9]{6}$/;
    return re.test(ifsc) || ifsc === '';
  };

  const validateAccountNumber = (accNo) => {
    const re = /^[0-9]{9,18}$/;
    return re.test(accNo) || accNo === '';
  };

  const validatePFNumber = (pf) => {
    const re = /^[A-Z]{2}[0-9]{4,7}$/;
    return re.test(pf) || pf === '';
  };

  const validateESINumber = (esi) => {
    const re = /^[0-9]{17}$/;
    return re.test(esi) || esi === '';
  };

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      if (token) {
        fetchEmployees();
      }
    }, searchTerm ? 500 : 0);

    return () => clearTimeout(delayDebounceFn);
  }, [currentPage, searchTerm, filters, token]);

  const fetchEmployees = async () => {
    try {
      setSearching(true);
      setLoading(true);
      
      const response = await getEmployees(
        token, 
        currentPage, 
        pageSize, 
        searchTerm,
        filters.type,
        filters.role,
        filters.department,
        filters.status
      );

      console.log("getEmployees response : ", response);

      if (response && response.data) {
        const transformedEmployees = response.data.map((employee, index) => ({
          id: employee._id || employee.id,
          serialNo: getSequentialNumber(index),
          profileImage: employee.profilePicture || employee.profileImage,
          name: employee.name || '',
          email: employee.email || '',
          mobileNumber: employee.mobile || employee.phone || employee.phoneNumber || '',
          type: employee.type || 'employee',
          role: employee.role || 'employee',
          department: employee.department || '',
          position: employee.position || '',
          isActive: employee.isActive || false,
          isApproved: employee.isApproved || false,
          status: employee.status || 'inactive',
          joined: new Date(employee.createdAt).toISOString().split('T')[0],
          payroll: employee.payroll || null
        }));

        setEmployees(transformedEmployees);
        setTotalPages(response.pages || 1);
        setTotalEmployees(response.total || 0);
      } else {
        setEmployees([]);
        setTotalPages(1);
        setTotalEmployees(0);
      }
    } catch (err) {
      setError('Failed to fetch users.');
      toast.error('Failed to fetch users. Please refresh the page.');
      console.error('Error fetching users:', err);
      setEmployees([]);
      setTotalPages(1);
      setTotalEmployees(0);
    } finally {
      setLoading(false);
      setSearching(false);
    }
  };

  const handlePayrollToggle = async (userId, newStatus) => {
    try {
      setPayrollToggling(prev => ({ ...prev, [userId]: true }));
      
      const response = await togglePayrollStatus(userId, newStatus, token);
      
      if (response && response.success) {
        setEmployees(prevEmployees =>
          prevEmployees.map(emp =>
            emp.id === userId ? {
              ...emp,
              payroll: {
                ...emp.payroll,
                status: newStatus
              }
            } : emp
          )
        );
        
        toast.success(`Payroll status updated to ${newStatus.charAt(0).toUpperCase() + newStatus.slice(1)}`);
        
        fetchEmployees();
      } else {
        const errorMsg = response?.message || 'Failed to update payroll status';
        toast.error(errorMsg);
      }
    } catch (error) {
      console.error('Error toggling payroll:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to update payroll status';
      toast.error(errorMessage);
    } finally {
      setPayrollToggling(prev => ({ ...prev, [userId]: false }));
    }
  };

  const handleApproveUser = async (userId, userType) => {
    if (!window.confirm(`Are you sure you want to approve this ${userType}?`)) {
      return;
    }

    try {
      setIsProcessing(true);
      setError(null);

      const response = await approveUser(userId, userType, token);

      if (response && response.success) {
        toast.success(`${userType === 'executive' ? 'Executive' : 'Employee'} approved successfully!`);
        
        setEmployees(prevEmployees =>
          prevEmployees.map(emp =>
            emp.id === userId ? {
              ...emp,
              status: 'active',
              isActive: true,
              isApproved: true
            } : emp
          )
        );
        
        fetchEmployees();
      } else {
        const errorMsg = response?.message || `Failed to approve ${userType}.`;
        setError(errorMsg);
        toast.error(errorMsg);
      }
    } catch (err) {
      console.error('Error approving user:', err);
      const errorMessage = err.response?.data?.message || err.message || 'Failed to approve user. Please try again.';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleRejectUser = async (userId, userType) => {
    if (!window.confirm(`Are you sure you want to reject this ${userType}? This action cannot be undone.`)) {
      return;
    }

    try {
      setIsProcessing(true);
      setError(null);

      const response = await rejectUser(userId, userType, token);

      if (response && response.success) {
        toast.success(`${userType === 'executive' ? 'Executive' : 'Employee'} rejected and removed.`);
        setEmployees(prevEmployees => prevEmployees.filter(emp => emp.id !== userId));
        fetchEmployees();
      } else {
        const errorMsg = response?.message || `Failed to reject ${userType}.`;
        setError(errorMsg);
        toast.error(errorMsg);
      }
    } catch (err) {
      console.error('Error rejecting user:', err);
      const errorMessage = err.response?.data?.message || err.message || 'Failed to reject user. Please try again.';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleViewEmployee = async (employeeId) => {
    try {
      setIsFetchingDetails(true);
      setError(null);
      
      const localEmployee = employees.find(emp => emp.id === employeeId);
      
      if (localEmployee) {
        const payrollData = localEmployee.payroll ? {
          employeeId: localEmployee.payroll.employeeId || null,
          designation: localEmployee.payroll.designation || null,
          dateOfJoining: localEmployee.payroll.dateOfJoining || null,
          salary: {
            basic: localEmployee.payroll.salary?.basic || 0,
            hra: localEmployee.payroll.salary?.hra || 0,
            allowances: localEmployee.payroll.salary?.allowances || 0,
            deductions: localEmployee.payroll.salary?.deductions || 0,
            netSalary: localEmployee.payroll.salary?.netSalary || 0,
            currency: localEmployee.payroll.salary?.currency || 'INR'
          },
          bankDetails: {
            accountHolderName: localEmployee.payroll.bankDetails?.accountHolderName || null,
            accountNumber: localEmployee.payroll.bankDetails?.accountNumber || null,
            ifscCode: localEmployee.payroll.bankDetails?.ifscCode || null,
            bankName: localEmployee.payroll.bankDetails?.bankName || null,
            branch: localEmployee.payroll.bankDetails?.branch || null
          },
          taxDetails: {
            panNumber: localEmployee.payroll.taxDetails?.panNumber || null,
            pfNumber: localEmployee.payroll.taxDetails?.pfNumber || null,
            esiNumber: localEmployee.payroll.taxDetails?.esiNumber || null
          },
          paymentMode: localEmployee.payroll.paymentMode || 'bank_transfer',
          paymentCycle: localEmployee.payroll.paymentCycle || 'monthly',
          status: localEmployee.payroll.status || 'inactive',
          lastPaidOn: localEmployee.payroll.lastPaidOn || null,
          remarks: localEmployee.payroll.remarks || null
        } : null;

        setViewingEmployee({
          id: localEmployee.id,
          name: localEmployee.name || '',
          email: localEmployee.email || '',
          mobileNumber: localEmployee.mobileNumber || '',
          type: localEmployee.type || 'employee',
          role: localEmployee.role || '',
          department: localEmployee.department || '',
          position: localEmployee.position || '',
          isActive: localEmployee.isActive,
          isApproved: localEmployee.isApproved,
          status: localEmployee.status || 'pending',
          profileImage: localEmployee.profileImage,
          payroll: payrollData,
          createdAt: localEmployee.joined || new Date().toISOString().split('T')[0],
          updatedAt: new Date().toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
          })
        });
      } else {
        const response = await getEmployeeById(employeeId, token);
        
        if (response && response.success) {
          const employee = response.data;
          const payrollData = employee.payroll ? {
            employeeId: employee.payroll.employeeId || null,
            designation: employee.payroll.designation || null,
            dateOfJoining: employee.payroll.dateOfJoining || null,
            salary: {
              basic: employee.payroll.salary?.basic || 0,
              hra: employee.payroll.salary?.hra || 0,
              allowances: employee.payroll.salary?.allowances || 0,
              deductions: employee.payroll.salary?.deductions || 0,
              netSalary: employee.payroll.salary?.netSalary || 0,
              currency: employee.payroll.salary?.currency || 'INR'
            },
            bankDetails: {
              accountHolderName: employee.payroll.bankDetails?.accountHolderName || null,
              accountNumber: employee.payroll.bankDetails?.accountNumber || null,
              ifscCode: employee.payroll.bankDetails?.ifscCode || null,
              bankName: employee.payroll.bankDetails?.bankName || null,
              branch: employee.payroll.bankDetails?.branch || null
            },
            taxDetails: {
              panNumber: employee.payroll.taxDetails?.panNumber || null,
              pfNumber: employee.payroll.taxDetails?.pfNumber || null,
              esiNumber: employee.payroll.taxDetails?.esiNumber || null
            },
            paymentMode: employee.payroll.paymentMode || 'bank_transfer',
            paymentCycle: employee.payroll.paymentCycle || 'monthly',
            status: employee.payroll.status || 'inactive',
            lastPaidOn: employee.payroll.lastPaidOn || null,
            remarks: employee.payroll.remarks || null
          } : null;

          setViewingEmployee({
            id: employee._id,
            name: employee.name || '',
            email: employee.email || '',
            mobileNumber: employee.mobile || employee.phone || employee.phoneNumber || '',
            type: employee.type || 'employee',
            role: employee.role || '',
            department: employee.department || '',
            position: employee.position || '',
            isActive: employee.isActive,
            isApproved: employee.isApproved,
            status: employee.status || 'pending',
            profileImage: employee.profilePicture,
            payroll: payrollData,
            createdAt: new Date(employee.createdAt).toLocaleDateString('en-US', {
              year: 'numeric',
              month: 'long',
              day: 'numeric'
            }),
            updatedAt: new Date(employee.updatedAt).toLocaleDateString('en-US', {
              year: 'numeric',
              month: 'long',
              day: 'numeric'
            })
          });
        } else {
          setError('Failed to fetch user details.');
          toast.error('Failed to fetch user details.');
        }
      }
    } catch (err) {
      console.error('Error fetching user details:', err);
      setError('Failed to fetch user details.');
      toast.error('Failed to fetch user details.');
    } finally {
      setIsFetchingDetails(false);
    }
  };

  const handleEditEmployee = (employeeId) => {
    const employee = employees.find(emp => emp.id === employeeId);
    
    if (employee) {
      const payrollData = employee.payroll || {
        employeeId: '',
        designation: '',
        dateOfJoining: '',
        salary: {
          basic: 0,
          hra: 0,
          allowances: 0,
          deductions: 0,
          netSalary: 0,
          currency: 'INR'
        },
        bankDetails: {
          accountHolderName: '',
          accountNumber: '',
          ifscCode: '',
          bankName: '',
          branch: ''
        },
        taxDetails: {
          panNumber: '',
          pfNumber: '',
          esiNumber: ''
        },
        paymentMode: 'bank_transfer',
        paymentCycle: 'monthly',
        status: 'inactive',
        lastPaidOn: '',
        remarks: ''
      };

      setEditForm({
        name: employee.name || '',
        email: employee.email || '',
        phoneNumber: employee.mobileNumber || '',
        type: employee.type || 'employee',
        role: employee.role || 'employee',
        department: employee.department || '',
        position: employee.position || '',
        isActive: employee.isActive || true,
        payroll: payrollData
      });
      
      setEditingEmployeeId(employeeId);
      setShowEditModal(true);
      setError(null);
      setValidationErrors({});
    } else {
      toast.error('User not found');
    }
  };

  const validateEditForm = () => {
    const errors = {};
    
    if (!editForm.name || editForm.name.trim().length < 2) {
      errors.name = 'Name must be at least 2 characters';
    }
    
    if (!editForm.email || !validateEmail(editForm.email)) {
      errors.email = 'Please enter a valid email address';
    }
    
    if (editForm.phoneNumber && !validatePhone(editForm.phoneNumber)) {
      errors.phoneNumber = 'Please enter a valid phone number';
    }
    
    if (editForm.payroll.bankDetails?.accountNumber && 
        !validateAccountNumber(editForm.payroll.bankDetails.accountNumber)) {
      errors.accountNumber = 'Account number must be 9-18 digits';
    }
    
    if (editForm.payroll.bankDetails?.ifscCode && 
        !validateIFSC(editForm.payroll.bankDetails.ifscCode)) {
      errors.ifscCode = 'Please enter a valid IFSC code (e.g., SBIN0001234)';
    }
    
    if (editForm.payroll.taxDetails?.panNumber && 
        !validatePAN(editForm.payroll.taxDetails.panNumber)) {
      errors.panNumber = 'Please enter a valid PAN number (e.g., ABCDE1234F)';
    }
    
    if (editForm.payroll.taxDetails?.pfNumber && 
        !validatePFNumber(editForm.payroll.taxDetails.pfNumber)) {
      errors.pfNumber = 'Please enter a valid PF number (e.g., AB12345)';
    }
    
    if (editForm.payroll.taxDetails?.esiNumber && 
        !validateESINumber(editForm.payroll.taxDetails.esiNumber)) {
      errors.esiNumber = 'Please enter a valid ESI number (17 digits)';
    }
    
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSaveEdit = async () => {
    if (!validateEditForm()) {
      toast.error('Please fix the validation errors');
      return;
    }

    try {
      setIsSavingEdit(true);
      setError(null);
      
      const basic = parseFloat(editForm.payroll.salary.basic) || 0;
      const hra = parseFloat(editForm.payroll.salary.hra) || 0;
      const allowances = parseFloat(editForm.payroll.salary.allowances) || 0;
      const deductions = parseFloat(editForm.payroll.salary.deductions) || 0;
      const netSalary = basic + hra + allowances - deductions;
      
      const updateData = {
        name: editForm.name,
        email: editForm.email,
        phoneNumber: editForm.phoneNumber,
        phone: editForm.phoneNumber,
        type: editForm.type,
        role: editForm.role,
        department: editForm.department,
        position: editForm.position,
        isActive: editForm.isActive,
        payroll: {
          employeeId: editForm.payroll.employeeId || '',
          designation: editForm.payroll.designation || '',
          dateOfJoining: editForm.payroll.dateOfJoining || '',
          salary: {
            basic: basic,
            hra: hra,
            allowances: allowances,
            deductions: deductions,
            netSalary: netSalary,
            currency: editForm.payroll.salary.currency || 'INR'
          },
          bankDetails: {
            accountHolderName: editForm.payroll.bankDetails?.accountHolderName || '',
            accountNumber: editForm.payroll.bankDetails?.accountNumber || '',
            ifscCode: editForm.payroll.bankDetails?.ifscCode || '',
            bankName: editForm.payroll.bankDetails?.bankName || '',
            branch: editForm.payroll.bankDetails?.branch || ''
          },
          taxDetails: {
            panNumber: editForm.payroll.taxDetails?.panNumber || '',
            pfNumber: editForm.payroll.taxDetails?.pfNumber || '',
            esiNumber: editForm.payroll.taxDetails?.esiNumber || ''
          },
          paymentMode: editForm.payroll.paymentMode || 'bank_transfer',
          paymentCycle: editForm.payroll.paymentCycle || 'monthly',
          status: editForm.payroll.status || 'inactive',
          lastPaidOn: editForm.payroll.lastPaidOn || null,
          remarks: editForm.payroll.remarks || ''
        }
      };

      console.log('Updating user:', editingEmployeeId, updateData);

      const response = await updateEmployee(editingEmployeeId, updateData, token);
      
      if (response && response.success) {
        setEmployees(prevEmployees =>
          prevEmployees.map(emp =>
            emp.id === editingEmployeeId ? {
              ...emp,
              name: response.data.name || editForm.name,
              email: response.data.email || editForm.email,
              mobileNumber: response.data.phone || editForm.phoneNumber,
              type: response.data.type || editForm.type,
              role: response.data.role || editForm.role,
              department: response.data.department || editForm.department,
              position: response.data.position || editForm.position,
              status: response.data.isActive ? 'active' : 'inactive',
              isActive: response.data.isActive,
              payroll: response.data.payroll || editForm.payroll
            } : emp
          )
        );

        setShowEditModal(false);
        setEditingEmployeeId(null);
        setEditForm({
          name: '',
          email: '',
          phoneNumber: '',
          type: 'employee',
          role: 'employee',
          department: '',
          position: '',
          isActive: true,
          payroll: {
            employeeId: '',
            designation: '',
            dateOfJoining: '',
            salary: {
              basic: 0,
              hra: 0,
              allowances: 0,
              deductions: 0,
              netSalary: 0,
              currency: 'INR'
            },
            bankDetails: {
              accountHolderName: '',
              accountNumber: '',
              ifscCode: '',
              bankName: '',
              branch: ''
            },
            taxDetails: {
              panNumber: '',
              pfNumber: '',
              esiNumber: ''
            },
            paymentMode: 'bank_transfer',
            paymentCycle: 'monthly',
            status: 'inactive',
            lastPaidOn: '',
            remarks: ''
          }
        });
        setValidationErrors({});
        
        toast.success(`${editForm.type === 'executive' ? 'Executive' : 'Employee'} updated successfully!`);
        fetchEmployees();
      } else {
        const errorMsg = response?.message || 'Failed to update user.';
        setError(errorMsg);
        toast.error(errorMsg);
      }
    } catch (err) {
      console.error('Error updating user:', err);
      const errorMessage = err.response?.data?.message || err.message || 'Failed to update user. Please try again.';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setIsSavingEdit(false);
    }
  };

  const handleCancelEdit = () => {
    setShowEditModal(false);
    setEditingEmployeeId(null);
    setEditForm({
      name: '',
      email: '',
      phoneNumber: '',
      type: 'employee',
      role: 'employee',
      department: '',
      position: '',
      isActive: true,
      payroll: {
        employeeId: '',
        designation: '',
        dateOfJoining: '',
        salary: {
          basic: 0,
          hra: 0,
          allowances: 0,
          deductions: 0,
          netSalary: 0,
          currency: 'INR'
        },
        bankDetails: {
          accountHolderName: '',
          accountNumber: '',
          ifscCode: '',
          bankName: '',
          branch: ''
        },
        taxDetails: {
          panNumber: '',
          pfNumber: '',
          esiNumber: ''
        },
        paymentMode: 'bank_transfer',
        paymentCycle: 'monthly',
        status: 'inactive',
        lastPaidOn: '',
        remarks: ''
      }
    });
    setValidationErrors({});
    setError(null);
  };

  const handleAddEmployee = async () => {
    if (!addForm.name || addForm.name.trim().length < 2) {
      toast.error('Name must be at least 2 characters');
      return;
    }
    if (!addForm.email || !validateEmail(addForm.email)) {
      toast.error('Please enter a valid email address');
      return;
    }
    if (!addForm.password || addForm.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    if (addForm.password !== addForm.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    try {
      setIsAddingEmployee(true);
      setError(null);
      
      const basic = parseFloat(addForm.payroll.salary.basic) || 0;
      const hra = parseFloat(addForm.payroll.salary.hra) || 0;
      const allowances = parseFloat(addForm.payroll.salary.allowances) || 0;
      const deductions = parseFloat(addForm.payroll.salary.deductions) || 0;
      const netSalary = basic + hra + allowances - deductions;
      
      const employeeData = {
        name: addForm.name,
        email: addForm.email,
        password: addForm.password,
        type: addForm.type,
        role: addForm.role,
        phoneNumber: addForm.phoneNumber,
        department: addForm.department,
        position: addForm.position,
        payroll: {
          ...addForm.payroll,
          salary: {
            ...addForm.payroll.salary,
            netSalary: netSalary
          }
        }
      };
      
      const response = await createEmployee(employeeData, token);
      
      if (response && response.success) {
        setShowAddModal(false);
        setAddForm({
          name: '',
          email: '',
          password: '',
          confirmPassword: '',
          type: 'employee',
          role: 'employee',
          phoneNumber: '',
          department: '',
          position: '',
          payroll: {
            employeeId: '',
            designation: '',
            dateOfJoining: '',
            salary: {
              basic: 0,
              hra: 0,
              allowances: 0,
              deductions: 0,
              netSalary: 0,
              currency: 'INR'
            },
            bankDetails: {
              accountHolderName: '',
              accountNumber: '',
              ifscCode: '',
              bankName: '',
              branch: ''
            },
            taxDetails: {
              panNumber: '',
              pfNumber: '',
              esiNumber: ''
            },
            paymentMode: 'bank_transfer',
            paymentCycle: 'monthly',
            status: 'inactive',
            lastPaidOn: '',
            remarks: ''
          }
        });
        
        setCurrentPage(1);
        setSearchTerm('');
        setFilters({ type: '', role: '', department: '', status: '' });
        
        toast.success(`${addForm.type === 'executive' ? 'Executive' : 'Employee'} created successfully!`);
        fetchEmployees();
      } else {
        let errorMsg = response?.message || 'Failed to create user.';
        if (errorMsg.toLowerCase().includes('email already exists')) {
          errorMsg = `User with email "${addForm.email}" already exists.`;
        }
        setError(errorMsg);
        toast.error(errorMsg);
      }
    } catch (err) {
      console.error('Error creating user:', err);
      let errorMessage = 'Failed to create user. Please try again.';
      if (err.response?.data?.message) {
        const serverMsg = err.response.data.message;
        if (serverMsg.toLowerCase().includes('email already exists')) {
          errorMessage = `User with email "${addForm.email}" already exists.`;
        } else {
          errorMessage = serverMsg;
        }
      }
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setIsAddingEmployee(false);
    }
  };

  const handleUpdatePassword = async () => {
    if (!passwordForm.newPassword || passwordForm.newPassword.length < 6) {
      toast.error('New password must be at least 6 characters long');
      return;
    }

    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    try {
      setIsUpdatingPassword(true);
      setError(null);
      
      const response = await updateEmployeePassword(
        selectedEmployeeId, 
        { newPassword: passwordForm.newPassword }, 
        token
      );
      
      if (response && response.success) {
        setShowPasswordModal(false);
        setPasswordForm({ newPassword: '', confirmPassword: '' });
        setSelectedEmployeeId(null);
        toast.success('Password updated successfully!');
        fetchEmployees();
      } else {
        setError(response?.message || 'Failed to update password.');
        toast.error(response?.message || 'Failed to update password.');
      }
    } catch (err) {
      console.error('Error updating password:', err);
      const errorMessage = err.response?.data?.message || 'Failed to update password. Please try again.';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setIsUpdatingPassword(false);
    }
  };

  const handleDeleteEmployee = async () => {
    try {
      setError(null);
      
      let response;
      if (deleteType === 'permanent') {
        response = await permanentDeleteEmployee(employeeToDelete, token);
      } else {
        response = await deleteEmployee(employeeToDelete, token);
      }
      
      if (response && response.success) {
        setShowDeleteConfirm(false);
        setEmployeeToDelete(null);
        toast.success(
          deleteType === 'permanent' 
            ? 'User permanently deleted successfully!' 
            : 'User deactivated successfully!'
        );
        fetchEmployees();
      } else {
        setError(response?.message || 'Failed to delete user.');
        toast.error(response?.message || 'Failed to delete user.');
      }
    } catch (err) {
      console.error('Error deleting user:', err);
      const errorMessage = err.response?.data?.message || 'Failed to delete user. Please try again.';
      setError(errorMessage);
      toast.error(errorMessage);
    }
  };

  const handleBulkCreate = async () => {
    try {
      setError(null);
      
      let employeesData;
      try {
        employeesData = JSON.parse(bulkEmployees);
        if (!Array.isArray(employeesData)) {
          throw new Error('Data must be an array');
        }
      } catch (e) {
        setError('Invalid JSON format. Please check your input.');
        toast.error('Invalid JSON format. Please check your input.');
        return;
      }
      
      const response = await bulkCreateEmployees(employeesData, token);
      
      if (response && response.success) {
        setShowBulkModal(false);
        setBulkEmployees('');
        toast.success(`${response.count || employeesData.length} users created successfully!`);
        fetchEmployees();
      } else {
        setError(response?.message || 'Failed to create users.');
        toast.error(response?.message || 'Failed to create users.');
      }
    } catch (err) {
      console.error('Error bulk creating users:', err);
      const errorMessage = err.response?.data?.message || 'Failed to create users. Please try again.';
      setError(errorMessage);
      toast.error(errorMessage);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (name.startsWith('payroll.')) {
      const path = name.split('.');
      setEditForm(prev => {
        const newForm = { ...prev };
        let current = newForm;
        for (let i = 0; i < path.length - 1; i++) {
          current = current[path[i]];
        }
        current[path[path.length - 1]] = type === 'checkbox' ? checked : value;
        return newForm;
      });
      
      const fieldName = path[path.length - 1];
      if (validationErrors[fieldName]) {
        setValidationErrors(prev => ({ ...prev, [fieldName]: '' }));
      }
    } else {
      setEditForm(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      }));
      
      if (validationErrors[name]) {
        setValidationErrors(prev => ({ ...prev, [name]: '' }));
      }
    }
  };

  const handleAddFormChange = (e) => {
    const { name, value } = e.target;
    
    if (name.startsWith('payroll.')) {
      const path = name.split('.');
      setAddForm(prev => {
        const newForm = { ...prev };
        let current = newForm;
        for (let i = 0; i < path.length - 1; i++) {
          current = current[path[i]];
        }
        current[path[path.length - 1]] = value;
        return newForm;
      });
    } else {
      setAddForm(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handlePasswordFormChange = (e) => {
    const { name, value } = e.target;
    setPasswordForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
    setCurrentPage(1);
  };

  const closeViewModal = () => {
    setViewingEmployee(null);
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const handleRefresh = () => {
    setCurrentPage(1);
    setSearchTerm('');
    setFilters({ type: '', role: '', department: '', status: '' });
    fetchEmployees();
  };

  const handleExportCSV = () => {
    const headers = ['Name', 'Email', 'Mobile', 'Type', 'Role', 'Department', 'Position', 'Status', 'Payroll Status', 'Joined'];
    const csvData = employees.map(emp => [
      emp.name,
      emp.email,
      emp.mobileNumber,
      emp.type || 'employee',
      emp.role,
      emp.department,
      emp.position,
      emp.status,
      emp.payroll?.status || 'inactive',
      emp.joined
    ]);
    
    const csvContent = [headers.join(','), ...csvData.map(row => row.join(','))].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `users_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
    
    toast.success('CSV exported successfully!');
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(prev => prev + 1);
    }
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(prev => prev - 1);
    }
  };

  const handlePageClick = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const handleCloseAddModal = () => {
    setShowAddModal(false);
    setAddForm({
      name: '',
      email: '',
      password: '',
      confirmPassword: '',
      type: 'employee',
      role: 'employee',
      phoneNumber: '',
      department: '',
      position: '',
      payroll: {
        employeeId: '',
        designation: '',
        dateOfJoining: '',
        salary: {
          basic: 0,
          hra: 0,
          allowances: 0,
          deductions: 0,
          netSalary: 0,
          currency: 'INR'
        },
        bankDetails: {
          accountHolderName: '',
          accountNumber: '',
          ifscCode: '',
          bankName: '',
          branch: ''
        },
        taxDetails: {
          panNumber: '',
          pfNumber: '',
          esiNumber: ''
        },
        paymentMode: 'bank_transfer',
        paymentCycle: 'monthly',
        status: 'inactive',
        lastPaidOn: '',
        remarks: ''
      }
    });
    setError(null);
  };

  const handleClosePasswordModal = () => {
    setShowPasswordModal(false);
    setPasswordForm({ newPassword: '', confirmPassword: '' });
    setSelectedEmployeeId(null);
    setError(null);
  };

  if (loading && employees.length === 0) {
    return (
      <div className="management-page">
        <ToastContainer 
          position="top-right"
          autoClose={5000}
          hideProgressBar={false}
          newestOnTop
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          theme="light"
        />
        <div className="page-header">
          <div className="skeleton-header-title"></div>
          <div className="header-actions">
            <div className="skeleton-search"></div>
            <div className="skeleton-button"></div>
            <div className="skeleton-button"></div>
            <div className="skeleton-button"></div>
            <div className="skeleton-button"></div>
          </div>
        </div>
        <div className="filters-section">
          <div className="skeleton-filter"></div>
          <div className="skeleton-filter"></div>
          <div className="skeleton-filter"></div>
          <div className="skeleton-filter"></div>
        </div>
        <div className="stats-cards">
          {[1, 2, 3, 4, 5].map((i) => (
            <SkeletonStatsCard key={i} />
          ))}
        </div>
        <SkeletonTable />
        <div className="pagination-container">
          <div className="skeleton-pagination-info"></div>
          <div className="skeleton-pagination-controls"></div>
        </div>
      </div>
    );
  }

  const pendingCount = employees.filter(e => e.status === 'pending').length;
  const activeCount = employees.filter(e => e.status === 'active').length;
  const inactiveCount = employees.filter(e => e.status === 'inactive').length;
  const employeeCount = employees.filter(e => e.type === 'employee').length;
  const executiveCount = employees.filter(e => e.type === 'executive').length;
  
  const payrollActiveCount = employees.filter(e => e.payroll?.status === 'active').length;
  const payrollInactiveCount = employees.filter(e => e.payroll?.status === 'inactive').length;
  const payrollOnHoldCount = employees.filter(e => e.payroll?.status === 'on_hold').length;

  return (
    <div className="management-page">
      <ToastContainer 
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />

      <div className="page-header">
        <h1>User Management</h1>
        <div className="header-actions">
          <div style={{ position: 'relative' }}>
            <input
              type="text"
              placeholder="Search users by name, email, mobile..."
              className="search-input"
              value={searchTerm}
              onChange={handleSearchChange}
            />
            {searching && (
              <span style={{
                position: 'absolute', right: '10px', top: '50%',
                transform: 'translateY(-50%)', fontSize: '12px', color: '#888'
              }}>
                searching...
              </span>
            )}
          </div>
          <button className="refresh-btn" onClick={handleRefresh}>
            Refresh
          </button>
          <button className="export-btn" onClick={handleExportCSV}>
            Export CSV
          </button>
          <button className="add-btn" onClick={() => setShowAddModal(true)}>
            + Add User
          </button>
          <button className="bulk-btn" onClick={() => setShowBulkModal(true)}>
            Bulk Create
          </button>
        </div>
      </div>


      <div className="filters-section">
        <select name="type" value={filters.type} onChange={handleFilterChange} className="filter-select">
          <option value="">All Types</option>
          {typeOptions.map(type => (
            <option key={type} value={type}>{type.toUpperCase()}</option>
          ))}
        </select>
        <select name="role" value={filters.role} onChange={handleFilterChange} className="filter-select">
          <option value="">All Roles</option>
          {roleOptions.map(role => (
            <option key={role} value={role}>{role}</option>
          ))}
        </select>
        <select name="department" value={filters.department} onChange={handleFilterChange} className="filter-select">
          <option value="">All Departments</option>
          {departmentOptions.map(dept => (
            <option key={dept} value={dept}>{dept}</option>
          ))}
        </select>
        <select name="status" value={filters.status} onChange={handleFilterChange} className="filter-select">
          <option value="">All Status</option>
          <option value="active">Active</option>
          <option value="pending">Pending</option>
          <option value="inactive">Inactive</option>
        </select>
      </div>

      {error && (
        <div className="error-message">
          {error}
        </div>
      )}


      <div className="stats-cards">
        <div className="stat-card">
          <h3>Total Users</h3>
          <p>{totalEmployees}</p>
          <span>All registered users</span>
        </div>
        <div className="stat-card">
          <h3>Active Users</h3>
          <p>{activeCount}</p>
          <span>Active accounts</span>
        </div>
        <div className="stat-card">
          <h3>Pending Users</h3>
          <p>{pendingCount}</p>
          <span>Awaiting approval</span>
        </div>
        <div className="stat-card">
          <h3>Payroll Active</h3>
          <p>{payrollActiveCount}</p>
          <span>Active payroll</span>
        </div>
      </div>


      <div className="table-container">
        <table className="data-table">
          <thead>
            <tr>
              <th>No.</th>
              <th>Name</th>
              <th>Email</th>
              <th>Mobile</th>
              <th>Type</th>
              <th>Role</th>
              <th>Department</th>
              <th>Position</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {employees.map(employee => {
              const isPending = employee.status === 'pending' || employee.status === 'Pending';
              const isActive = employee.status === 'active' || employee.status === 'Active';
              const payrollStatus = employee.payroll?.status || 'inactive';
              const isToggling = payrollToggling[employee.id] || false;
              
              return (
                <tr key={employee.id}>
                  <td>{employee.serialNo}</td>
                  <td>
                    <div className="employee-name-cell">
                      {employee.profileImage && (
                        <img 
                          src={`${process.env.REACT_APP_BASE_URL}/uploads/${employee.profileImage}`}
                          alt={employee.name}
                          className="employee-avatar-small"
                          onError={(e) => e.target.style.display = 'none'}
                        />
                      )}
                      <span>{employee.name || 'N/A'}</span>
                    </div>
                  </td>
                  <td>{employee.email || 'N/A'}</td>
                  <td>{employee.mobileNumber || 'N/A'}</td>
                  <td>
                    <span className={`type-badge type-${employee.type}`}>
                      {employee.type === 'executive' ? 'Executive' : 'Employee'}
                    </span>
                  </td>
                  <td>
                    <span className={`role-badge role-${employee.role}`}>
                      {employee.role || 'N/A'}
                    </span>
                  </td>
                  <td>{employee.department || 'N/A'}</td>
                  <td>{employee.position || 'N/A'}</td>
                  <td>
                    <div className="status-cell">
                      <span className={`status-badge ${isPending ? 'pending' : isActive ? 'active' : 'inactive'}`}>
                        {isPending ? 'Pending' : isActive ? 'Active' : 'Inactive'}
                      </span>

                      {!isPending && (
                        <div className="payroll-toggle-wrapper">
                          <span className="payroll-label">Payroll:</span>
                          <div className="payroll-toggle-buttons">
                            <button
                              className={`payroll-status-btn ${payrollStatus === 'active' ? 'active' : ''}`}
                              onClick={() => handlePayrollToggle(employee.id, 'active')}
                              disabled={isToggling}
                              title="Activate Payroll"
                            >
                              {isToggling && payrollStatus !== 'active' ? '...' : 'Active'}
                            </button>
                            <button
                              className={`payroll-status-btn ${payrollStatus === 'inactive' ? 'inactive' : ''}`}
                              onClick={() => handlePayrollToggle(employee.id, 'inactive')}
                              disabled={isToggling}
                              title="Deactivate Payroll"
                            >
                              {isToggling && payrollStatus !== 'inactive' ? '...' : 'Inactive'}
                            </button>
                            <button
                              className={`payroll-status-btn ${payrollStatus === 'on_hold' ? 'on-hold' : ''}`}
                              onClick={() => handlePayrollToggle(employee.id, 'on_hold')}
                              disabled={isToggling}
                              title="Put Payroll On Hold"
                            >
                              {isToggling && payrollStatus !== 'on_hold' ? '...' : 'Hold'}
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  </td>
                  <td>
                    <div className="action-buttons">

                      <button
                        className="action-btn view"
                        onClick={() => handleViewEmployee(employee.id)}
                        disabled={isFetchingDetails}
                      >
                        View
                      </button>


                      {!isPending && (
                        <button
                          className="action-btn edit"
                          onClick={() => handleEditEmployee(employee.id)}
                          disabled={isFetchingDetails}
                        >
                          Edit
                        </button>
                      )}


                      {isPending && (
                        <>
                          <button
                            className="action-btn approve"
                            onClick={() => handleApproveUser(employee.id, employee.type)}
                            disabled={isProcessing || isFetchingDetails}
                            title="Approve this user"
                          >
                            {isProcessing ? '...' : 'Approve'}
                          </button>
                          <button
                            className="action-btn reject"
                            onClick={() => handleRejectUser(employee.id, employee.type)}
                            disabled={isProcessing || isFetchingDetails}
                            title="Reject and remove this user"
                          >
                            {isProcessing ? '...' : 'Reject'}
                          </button>
                        </>
                      )}


                      {!isPending && (
                        <button
                          className="action-btn password"
                          onClick={() => {
                            setSelectedEmployeeId(employee.id);
                            setShowPasswordModal(true);
                          }}
                          disabled={isFetchingDetails}
                        >
                          Password
                        </button>
                      )}


                      {!isPending && (
                        <>
                          <button
                            className="action-btn delete"
                            onClick={() => {
                              setEmployeeToDelete(employee.id);
                              setDeleteType('soft');
                              setShowDeleteConfirm(true);
                            }}
                            disabled={isFetchingDetails}
                          >
                            Deactivate
                          </button>
                          <button
                            className="action-btn permanent-delete"
                            onClick={() => {
                              setEmployeeToDelete(employee.id);
                              setDeleteType('permanent');
                              setShowDeleteConfirm(true);
                            }}
                            disabled={isFetchingDetails}
                            title="Permanently Delete"
                          >
                            Delete
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>

        {employees.length === 0 && !loading && (
          <div className="no-data">
            <p>No users found matching your search.</p>
            {searchTerm && (
              <button className="clear-filters-link" onClick={handleRefresh}>
                Clear search to see all users
              </button>
            )}
          </div>
        )}
      </div>


      <div className="pagination-container">
        <div className="pagination-info">
          Showing {employees.length} of {totalEmployees} users (Page {currentPage} of {totalPages})
          {searchTerm && <span className="filtered-indicator"> • Search: "{searchTerm}"</span>}
        </div>
        <div className="pagination-controls">
          <button
            className="pagination-btn"
            onClick={handlePrevPage}
            disabled={currentPage === 1}
          >
            Previous
          </button>

          <div className="page-numbers">
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              let pageNumber;
              if (totalPages <= 5) {
                pageNumber = i + 1;
              } else if (currentPage <= 3) {
                pageNumber = i + 1;
              } else if (currentPage >= totalPages - 2) {
                pageNumber = totalPages - 4 + i;
              } else {
                pageNumber = currentPage - 2 + i;
              }

              return (
                <button
                  key={pageNumber}
                  className={`page-number ${pageNumber === currentPage ? 'active' : ''}`}
                  onClick={() => handlePageClick(pageNumber)}
                >
                  {pageNumber}
                </button>
              );
            })}
          </div>

          <button
            className="pagination-btn"
            onClick={handleNextPage}
            disabled={currentPage === totalPages}
          >
            Next
          </button>
        </div>
      </div>


      {viewingEmployee && !isFetchingDetails && (
        <div className="modal-overlay" onClick={closeViewModal}>
          <div className="modal-content1" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{viewingEmployee.type === 'executive' ? 'Executive' : 'Employee'} Details</h3>
              <button className="modal-close-btn" onClick={closeViewModal}>
                ×
              </button>
            </div>
            <div className="modal-body" style={{ maxHeight: '80vh', overflowY: 'auto' }}>
              <div className="customer-details">
                {viewingEmployee.profileImage && (
                  <div className="customer-profile-image">
                    <img
                      src={`${process.env.REACT_APP_BASE_URL}/uploads/${viewingEmployee.profileImage}`}
                      alt={viewingEmployee.name}
                      className="profile-image-large"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.style.display = 'none';
                      }}
                    />
                  </div>
                )}

                <h4 style={{ marginTop: '10px', marginBottom: '15px', color: '#333', borderBottom: '2px solid #eee', paddingBottom: '10px' }}>
                  Basic Information
                </h4>

                <div className="detail-row">
                  <label>Name:</label>
                  <span>{viewingEmployee.name || 'N/A'}</span>
                </div>
                <div className="detail-row">
                  <label>Email:</label>
                  <span>{viewingEmployee.email || 'N/A'}</span>
                </div>
                <div className="detail-row">
                  <label>Mobile Number:</label>
                  <span>{viewingEmployee.mobileNumber || 'N/A'}</span>
                </div>
                <div className="detail-row">
                  <label>Type:</label>
                  <span className={`type-badge type-${viewingEmployee.type}`}>
                    {viewingEmployee.type === 'executive' ? 'Executive' : 'Employee'}
                  </span>
                </div>
                <div className="detail-row">
                  <label>Role:</label>
                  <span>{viewingEmployee.role || 'N/A'}</span>
                </div>
                <div className="detail-row">
                  <label>Department:</label>
                  <span>{viewingEmployee.department || 'N/A'}</span>
                </div>
                <div className="detail-row">
                  <label>Position:</label>
                  <span>{viewingEmployee.position || 'N/A'}</span>
                </div>
                <div className="detail-row">
                  <label>Status:</label>
                  <span className={`status-badge ${
                    viewingEmployee.status === 'pending' ? 'pending' : 
                    viewingEmployee.isActive ? 'active' : 'inactive'
                  }`}>
                    {viewingEmployee.status === 'pending' ? 'Pending' : 
                     viewingEmployee.isActive ? 'Active' : 'Inactive'}
                  </span>
                </div>


                {viewingEmployee.payroll && (
                  <>
                    <h4 style={{ marginTop: '25px', marginBottom: '15px', color: '#333', borderBottom: '2px solid #eee', paddingBottom: '10px' }}>
                      Payroll Information
                    </h4>

                    <div className="detail-row">
                      <label>Employee ID:</label>
                      <span>{viewingEmployee.payroll.employeeId || 'N/A'}</span>
                    </div>
                    <div className="detail-row">
                      <label>Designation:</label>
                      <span>{viewingEmployee.payroll.designation || 'N/A'}</span>
                    </div>
                    <div className="detail-row">
                      <label>Date of Joining:</label>
                      <span>{viewingEmployee.payroll.dateOfJoining ? new Date(viewingEmployee.payroll.dateOfJoining).toLocaleDateString() : 'N/A'}</span>
                    </div>
                    <div className="detail-row">
                      <label>Payroll Status:</label>
                      <span className={`status-badge ${
                        viewingEmployee.payroll.status === 'active' ? 'active' : 
                        viewingEmployee.payroll.status === 'on_hold' ? 'on-hold' : 'inactive'
                      }`}>
                        {viewingEmployee.payroll.status ? viewingEmployee.payroll.status.toUpperCase() : 'INACTIVE'}
                      </span>
                    </div>

                    <h5 style={{ marginTop: '15px', marginBottom: '10px', color: '#555' }}>Salary Details</h5>
                    <div className="detail-row">
                      <label>Basic Salary:</label>
                      <span>
                        {viewingEmployee.payroll.salary?.currency || 'INR'} {viewingEmployee.payroll.salary?.basic || 0}
                      </span>
                    </div>
                    <div className="detail-row">
                      <label>HRA:</label>
                      <span>
                        {viewingEmployee.payroll.salary?.currency || 'INR'} {viewingEmployee.payroll.salary?.hra || 0}
                      </span>
                    </div>
                    <div className="detail-row">
                      <label>Allowances:</label>
                      <span>
                        {viewingEmployee.payroll.salary?.currency || 'INR'} {viewingEmployee.payroll.salary?.allowances || 0}
                      </span>
                    </div>
                    <div className="detail-row">
                      <label>Deductions:</label>
                      <span>
                        {viewingEmployee.payroll.salary?.currency || 'INR'} {viewingEmployee.payroll.salary?.deductions || 0}
                      </span>
                    </div>
                    <div className="detail-row">
                      <label>Net Salary:</label>
                      <span style={{ fontWeight: 'bold', color: '#2e7d32' }}>
                        {viewingEmployee.payroll.salary?.currency || 'INR'} {viewingEmployee.payroll.salary?.netSalary || 0}
                      </span>
                    </div>


                    {viewingEmployee.payroll.bankDetails && (
                      <>
                        <h5 style={{ marginTop: '15px', marginBottom: '10px', color: '#555' }}>Bank Details</h5>
                        <div className="detail-row">
                          <label>Account Holder Name:</label>
                          <span>{viewingEmployee.payroll.bankDetails.accountHolderName || 'N/A'}</span>
                        </div>
                        <div className="detail-row">
                          <label>Account Number:</label>
                          <span>{viewingEmployee.payroll.bankDetails.accountNumber || 'N/A'}</span>
                        </div>
                        <div className="detail-row">
                          <label>IFSC Code:</label>
                          <span>{viewingEmployee.payroll.bankDetails.ifscCode || 'N/A'}</span>
                        </div>
                        <div className="detail-row">
                          <label>Bank Name:</label>
                          <span>{viewingEmployee.payroll.bankDetails.bankName || 'N/A'}</span>
                        </div>
                        <div className="detail-row">
                          <label>Branch:</label>
                          <span>{viewingEmployee.payroll.bankDetails.branch || 'N/A'}</span>
                        </div>
                      </>
                    )}


                    {viewingEmployee.payroll.taxDetails && (
                      <>
                        <h5 style={{ marginTop: '15px', marginBottom: '10px', color: '#555' }}>Tax Details</h5>
                        <div className="detail-row">
                          <label>PAN Number:</label>
                          <span>{viewingEmployee.payroll.taxDetails.panNumber || 'N/A'}</span>
                        </div>
                        <div className="detail-row">
                          <label>PF Number:</label>
                          <span>{viewingEmployee.payroll.taxDetails.pfNumber || 'N/A'}</span>
                        </div>
                        <div className="detail-row">
                          <label>ESI Number:</label>
                          <span>{viewingEmployee.payroll.taxDetails.esiNumber || 'N/A'}</span>
                        </div>
                      </>
                    )}

                    <h5 style={{ marginTop: '15px', marginBottom: '10px', color: '#555' }}>Payment Settings</h5>
                    <div className="detail-row">
                      <label>Payment Mode:</label>
                      <span>{viewingEmployee.payroll.paymentMode ? viewingEmployee.payroll.paymentMode.replace('_', ' ').toUpperCase() : 'N/A'}</span>
                    </div>
                    <div className="detail-row">
                      <label>Payment Cycle:</label>
                      <span>{viewingEmployee.payroll.paymentCycle ? viewingEmployee.payroll.paymentCycle.toUpperCase() : 'N/A'}</span>
                    </div>
                    <div className="detail-row">
                      <label>Remarks:</label>
                      <span>{viewingEmployee.payroll.remarks || 'N/A'}</span>
                    </div>
                  </>
                )}

                <h4 style={{ marginTop: '25px', marginBottom: '15px', color: '#333', borderBottom: '2px solid #eee', paddingBottom: '10px' }}>
                  System Information
                </h4>
                <div className="detail-row">
                  <label>Created Date:</label>
                  <span>{viewingEmployee.createdAt}</span>
                </div>
                <div className="detail-row">
                  <label>Last Updated:</label>
                  <span>{viewingEmployee.updatedAt}</span>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="modal-close-btn secondary" onClick={closeViewModal}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {viewingEmployee && isFetchingDetails && <SkeletonModal />}


      {showEditModal && (
        <div className="modal-overlay" onClick={handleCancelEdit}>
          <div className="modal-content1 edit-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Edit {editForm.type === 'executive' ? 'Executive' : 'Employee'}</h3>
              <button className="modal-close-btn" onClick={handleCancelEdit}>
                ×
              </button>
            </div>
            <div className="modal-body" style={{ maxHeight: '80vh', overflowY: 'auto' }}>
              <div className="edit-form">

                <h4 style={{ marginBottom: '15px', color: '#333', borderBottom: '2px solid #eee', paddingBottom: '10px' }}>
                  Basic Information
                </h4>

                <div className="form-group">
                  <label>Type:*</label>
                  <select
                    name="type"
                    value={editForm.type || 'employee'}
                    onChange={handleInputChange}
                    className="form-control"
                  >
                    {typeOptions.map(type => (
                      <option key={type} value={type}>{type.toUpperCase()}</option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label>Name:*</label>
                  <input
                    type="text"
                    name="name"
                    value={editForm.name || ''}
                    onChange={handleInputChange}
                    className={`form-control ${validationErrors.name ? 'error' : ''}`}
                    placeholder="Full Name"
                  />
                  {validationErrors.name && (
                    <small className="error-text">{validationErrors.name}</small>
                  )}
                </div>

                <div className="form-group">
                  <label>Email:*</label>
                  <input
                    type="email"
                    name="email"
                    value={editForm.email || ''}
                    onChange={handleInputChange}
                    className={`form-control ${validationErrors.email ? 'error' : ''}`}
                    placeholder="Email Address"
                  />
                  {validationErrors.email && (
                    <small className="error-text">{validationErrors.email}</small>
                  )}
                </div>

                <div className="form-group">
                  <label>Phone Number:</label>
                  <input
                    type="text"
                    name="phoneNumber"
                    value={editForm.phoneNumber || ''}
                    onChange={handleInputChange}
                    className={`form-control ${validationErrors.phoneNumber ? 'error' : ''}`}
                    placeholder="Phone Number"
                  />
                  {validationErrors.phoneNumber && (
                    <small className="error-text">{validationErrors.phoneNumber}</small>
                  )}
                  <small className="form-text">Format: +91XXXXXXXXXX or 10 digit number</small>
                </div>

                <div className="form-group">
                  <label>Role:</label>
                  <select
                    name="role"
                    value={editForm.role || 'employee'}
                    onChange={handleInputChange}
                    className="form-control"
                  >
                    {roleOptions.map(role => (
                      <option key={role} value={role}>{role}</option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label>Department:</label>
                  <select
                    name="department"
                    value={editForm.department || ''}
                    onChange={handleInputChange}
                    className="form-control"
                  >
                    <option value="">Select Department</option>
                    {departmentOptions.map(dept => (
                      <option key={dept} value={dept}>{dept}</option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label>Position:</label>
                  <input
                    type="text"
                    name="position"
                    value={editForm.position || ''}
                    onChange={handleInputChange}
                    className="form-control"
                    placeholder="Position"
                  />
                </div>

                <div className="form-group checkbox-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      name="isActive"
                      checked={editForm.isActive || false}
                      onChange={handleInputChange}
                    />
                    <span className="checkbox-text">Active User</span>
                  </label>
                </div>


                <h4 style={{ marginTop: '25px', marginBottom: '15px', color: '#333', borderBottom: '2px solid #eee', paddingBottom: '10px' }}>
                  Payroll Information
                </h4>

                <div className="form-group">
                  <label>Designation:</label>
                  <input
                    type="text"
                    name="payroll.designation"
                    value={editForm.payroll?.designation || ''}
                    onChange={handleInputChange}
                    className="form-control"
                    placeholder="Designation"
                  />
                </div>

                <div className="form-group">
                  <label>Date of Joining:</label>
                  <input
                    type="date"
                    name="payroll.dateOfJoining"
                    value={editForm.payroll?.dateOfJoining ? new Date(editForm.payroll.dateOfJoining).toISOString().split('T')[0] : ''}
                    onChange={handleInputChange}
                    className="form-control"
                  />
                </div>

                <div className="form-group">
                  <label>Payroll Status:</label>
                  <select
                    name="payroll.status"
                    value={editForm.payroll?.status || 'inactive'}
                    onChange={handleInputChange}
                    className="form-control"
                  >
                    {payrollStatusOptions.map(status => (
                      <option key={status} value={status}>{status.toUpperCase()}</option>
                    ))}
                  </select>
                </div>


                <h5 style={{ marginTop: '20px', marginBottom: '10px', color: '#555' }}>Salary Details</h5>

                <div className="form-group">
                  <label>Basic Salary:</label>
                  <input
                    type="number"
                    name="payroll.salary.basic"
                    value={editForm.payroll?.salary?.basic || 0}
                    onChange={handleInputChange}
                    className="form-control"
                    placeholder="Basic Salary"
                    min="0"
                  />
                </div>

                <div className="form-group">
                  <label>HRA:</label>
                  <input
                    type="number"
                    name="payroll.salary.hra"
                    value={editForm.payroll?.salary?.hra || 0}
                    onChange={handleInputChange}
                    className="form-control"
                    placeholder="HRA"
                    min="0"
                  />
                </div>

                <div className="form-group">
                  <label>Allowances:</label>
                  <input
                    type="number"
                    name="payroll.salary.allowances"
                    value={editForm.payroll?.salary?.allowances || 0}
                    onChange={handleInputChange}
                    className="form-control"
                    placeholder="Allowances"
                    min="0"
                  />
                </div>

                <div className="form-group">
                  <label>Deductions:</label>
                  <input
                    type="number"
                    name="payroll.salary.deductions"
                    value={editForm.payroll?.salary?.deductions || 0}
                    onChange={handleInputChange}
                    className="form-control"
                    placeholder="Deductions"
                    min="0"
                  />
                </div>

                <div className="form-group">
                  <label>Currency:</label>
                  <select
                    name="payroll.salary.currency"
                    value={editForm.payroll?.salary?.currency || 'INR'}
                    onChange={handleInputChange}
                    className="form-control"
                  >
                    {currencyOptions.map(currency => (
                      <option key={currency} value={currency}>{currency}</option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label>Net Salary (Auto-calculated):</label>
                  <input
                    type="number"
                    name="payroll.salary.netSalary"
                    value={
                      (parseFloat(editForm.payroll?.salary?.basic) || 0) +
                      (parseFloat(editForm.payroll?.salary?.hra) || 0) +
                      (parseFloat(editForm.payroll?.salary?.allowances) || 0) -
                      (parseFloat(editForm.payroll?.salary?.deductions) || 0)
                    }
                    className="form-control"
                    readOnly
                    style={{ backgroundColor: '#f5f5f5' }}
                  />
                </div>


                <h5 style={{ marginTop: '20px', marginBottom: '10px', color: '#555' }}>Bank Details</h5>

                <div className="form-group">
                  <label>Account Holder Name:</label>
                  <input
                    type="text"
                    name="payroll.bankDetails.accountHolderName"
                    value={editForm.payroll?.bankDetails?.accountHolderName || ''}
                    onChange={handleInputChange}
                    className="form-control"
                    placeholder="Account Holder Name"
                  />
                </div>

                <div className="form-group">
                  <label>Account Number:</label>
                  <input
                    type="text"
                    name="payroll.bankDetails.accountNumber"
                    value={editForm.payroll?.bankDetails?.accountNumber || ''}
                    onChange={handleInputChange}
                    className={`form-control ${validationErrors.accountNumber ? 'error' : ''}`}
                    placeholder="Account Number (9-18 digits)"
                  />
                  {validationErrors.accountNumber && (
                    <small className="error-text">{validationErrors.accountNumber}</small>
                  )}
                  <small className="form-text">Enter 9-18 digit account number</small>
                </div>

                <div className="form-group">
                  <label>IFSC Code:</label>
                  <input
                    type="text"
                    name="payroll.bankDetails.ifscCode"
                    value={editForm.payroll?.bankDetails?.ifscCode || ''}
                    onChange={handleInputChange}
                    className={`form-control ${validationErrors.ifscCode ? 'error' : ''}`}
                    placeholder="IFSC Code (e.g., SBIN0001234)"
                  />
                  {validationErrors.ifscCode && (
                    <small className="error-text">{validationErrors.ifscCode}</small>
                  )}
                  <small className="form-text">Format: 4 letters + 0 + 6 alphanumeric</small>
                </div>

                <div className="form-group">
                  <label>Bank Name:</label>
                  <input
                    type="text"
                    name="payroll.bankDetails.bankName"
                    value={editForm.payroll?.bankDetails?.bankName || ''}
                    onChange={handleInputChange}
                    className="form-control"
                    placeholder="Bank Name"
                  />
                </div>

                <div className="form-group">
                  <label>Branch:</label>
                  <input
                    type="text"
                    name="payroll.bankDetails.branch"
                    value={editForm.payroll?.bankDetails?.branch || ''}
                    onChange={handleInputChange}
                    className="form-control"
                    placeholder="Branch"
                  />
                </div>


                <h5 style={{ marginTop: '20px', marginBottom: '10px', color: '#555' }}>Tax Details</h5>

                <div className="form-group">
                  <label>PAN Number:</label>
                  <input
                    type="text"
                    name="payroll.taxDetails.panNumber"
                    value={editForm.payroll?.taxDetails?.panNumber || ''}
                    onChange={handleInputChange}
                    className={`form-control ${validationErrors.panNumber ? 'error' : ''}`}
                    placeholder="PAN Number (e.g., ABCDE1234F)"
                    style={{ textTransform: 'uppercase' }}
                  />
                  {validationErrors.panNumber && (
                    <small className="error-text">{validationErrors.panNumber}</small>
                  )}
                  <small className="form-text">Format: 5 letters + 4 digits + 1 letter</small>
                </div>

                <div className="form-group">
                  <label>PF Number:</label>
                  <input
                    type="text"
                    name="payroll.taxDetails.pfNumber"
                    value={editForm.payroll?.taxDetails?.pfNumber || ''}
                    onChange={handleInputChange}
                    className={`form-control ${validationErrors.pfNumber ? 'error' : ''}`}
                    placeholder="PF Number (e.g., AB12345)"
                    style={{ textTransform: 'uppercase' }}
                  />
                  {validationErrors.pfNumber && (
                    <small className="error-text">{validationErrors.pfNumber}</small>
                  )}
                  <small className="form-text">Format: 2 letters + 4-7 digits</small>
                </div>

                <div className="form-group">
                  <label>ESI Number:</label>
                  <input
                    type="text"
                    name="payroll.taxDetails.esiNumber"
                    value={editForm.payroll?.taxDetails?.esiNumber || ''}
                    onChange={handleInputChange}
                    className={`form-control ${validationErrors.esiNumber ? 'error' : ''}`}
                    placeholder="ESI Number (17 digits)"
                  />
                  {validationErrors.esiNumber && (
                    <small className="error-text">{validationErrors.esiNumber}</small>
                  )}
                  <small className="form-text">Enter 17 digit ESI number</small>
                </div>


                <h5 style={{ marginTop: '20px', marginBottom: '10px', color: '#555' }}>Payment Settings</h5>

                <div className="form-group">
                  <label>Payment Mode:</label>
                  <select
                    name="payroll.paymentMode"
                    value={editForm.payroll?.paymentMode || 'bank_transfer'}
                    onChange={handleInputChange}
                    className="form-control"
                  >
                    {paymentModeOptions.map(mode => (
                      <option key={mode} value={mode}>{mode.replace('_', ' ').toUpperCase()}</option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label>Payment Cycle:</label>
                  <select
                    name="payroll.paymentCycle"
                    value={editForm.payroll?.paymentCycle || 'monthly'}
                    onChange={handleInputChange}
                    className="form-control"
                  >
                    {paymentCycleOptions.map(cycle => (
                      <option key={cycle} value={cycle}>{cycle.toUpperCase()}</option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label>Remarks:</label>
                  <textarea
                    name="payroll.remarks"
                    value={editForm.payroll?.remarks || ''}
                    onChange={handleInputChange}
                    className="form-control"
                    placeholder="Additional remarks"
                    rows="3"
                  />
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button 
                className="action-btn cancel" 
                onClick={handleCancelEdit}
                disabled={isSavingEdit}
              >
                Cancel
              </button>
              <button 
                className="action-btn save" 
                onClick={handleSaveEdit}
                disabled={isSavingEdit}
              >
                {isSavingEdit ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}


      {showAddModal && (
        <div className="modal-overlay" onClick={handleCloseAddModal}>
          <div className="modal-content1 add-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Add New User</h3>
              <button className="modal-close-btn" onClick={handleCloseAddModal}>
                ×
              </button>
            </div>
            <div className="modal-body" style={{ maxHeight: '80vh', overflowY: 'auto' }}>
              <div className="edit-form">

                <h4 style={{ marginBottom: '15px', color: '#333', borderBottom: '2px solid #eee', paddingBottom: '10px' }}>
                  Basic Information
                </h4>

                <div className="form-group">
                  <label>Type:*</label>
                  <select
                    name="type"
                    value={addForm.type}
                    onChange={handleAddFormChange}
                    className="form-control"
                    required
                  >
                    {typeOptions.map(type => (
                      <option key={type} value={type}>{type.toUpperCase()}</option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label>Name:*</label>
                  <input
                    type="text"
                    name="name"
                    value={addForm.name}
                    onChange={handleAddFormChange}
                    className="form-control"
                    placeholder="Full Name"
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Email:*</label>
                  <input
                    type="email"
                    name="email"
                    value={addForm.email}
                    onChange={handleAddFormChange}
                    className="form-control"
                    placeholder="Email Address"
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Password:*</label>
                  <input
                    type="password"
                    name="password"
                    value={addForm.password}
                    onChange={handleAddFormChange}
                    className="form-control"
                    placeholder="Password (min. 6 characters)"
                    required
                  />
                  <small className="form-text">Password must be at least 6 characters long</small>
                </div>

                <div className="form-group">
                  <label>Confirm Password:*</label>
                  <input
                    type="password"
                    name="confirmPassword"
                    value={addForm.confirmPassword || ''}
                    onChange={handleAddFormChange}
                    className="form-control"
                    placeholder="Confirm Password"
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Phone Number:</label>
                  <input
                    type="text"
                    name="phoneNumber"
                    value={addForm.phoneNumber}
                    onChange={handleAddFormChange}
                    className="form-control"
                    placeholder="Phone Number"
                  />
                  <small className="form-text">Format: +91XXXXXXXXXX or 10 digit number</small>
                </div>

                <div className="form-group">
                  <label>Role:*</label>
                  <select
                    name="role"
                    value={addForm.role}
                    onChange={handleAddFormChange}
                    className="form-control"
                    required
                  >
                    {roleOptions.map(role => (
                      <option key={role} value={role}>{role}</option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label>Department:</label>
                  <select
                    name="department"
                    value={addForm.department}
                    onChange={handleAddFormChange}
                    className="form-control"
                  >
                    <option value="">Select Department</option>
                    {departmentOptions.map(dept => (
                      <option key={dept} value={dept}>{dept}</option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label>Position:</label>
                  <input
                    type="text"
                    name="position"
                    value={addForm.position}
                    onChange={handleAddFormChange}
                    className="form-control"
                    placeholder="Position"
                  />
                </div>


                <h4 style={{ marginTop: '25px', marginBottom: '15px', color: '#333', borderBottom: '2px solid #eee', paddingBottom: '10px' }}>
                  Payroll Information
                </h4>

                <div className="form-group">
                  <label>Designation:</label>
                  <input
                    type="text"
                    name="payroll.designation"
                    value={addForm.payroll.designation}
                    onChange={handleAddFormChange}
                    className="form-control"
                    placeholder="Designation"
                  />
                </div>

                <div className="form-group">
                  <label>Date of Joining:</label>
                  <input
                    type="date"
                    name="payroll.dateOfJoining"
                    value={addForm.payroll.dateOfJoining}
                    onChange={handleAddFormChange}
                    className="form-control"
                  />
                </div>

                <div className="form-group">
                  <label>Payroll Status:</label>
                  <select
                    name="payroll.status"
                    value={addForm.payroll.status || 'inactive'}
                    onChange={handleAddFormChange}
                    className="form-control"
                  >
                    {payrollStatusOptions.map(status => (
                      <option key={status} value={status}>{status.toUpperCase()}</option>
                    ))}
                  </select>
                </div>


                <h5 style={{ marginTop: '20px', marginBottom: '10px', color: '#555' }}>Salary Details</h5>

                <div className="form-group">
                  <label>Basic Salary:</label>
                  <input
                    type="number"
                    name="payroll.salary.basic"
                    value={addForm.payroll.salary.basic}
                    onChange={handleAddFormChange}
                    className="form-control"
                    placeholder="Basic Salary"
                    min="0"
                  />
                </div>

                <div className="form-group">
                  <label>HRA:</label>
                  <input
                    type="number"
                    name="payroll.salary.hra"
                    value={addForm.payroll.salary.hra}
                    onChange={handleAddFormChange}
                    className="form-control"
                    placeholder="HRA"
                    min="0"
                  />
                </div>

                <div className="form-group">
                  <label>Allowances:</label>
                  <input
                    type="number"
                    name="payroll.salary.allowances"
                    value={addForm.payroll.salary.allowances}
                    onChange={handleAddFormChange}
                    className="form-control"
                    placeholder="Allowances"
                    min="0"
                  />
                </div>

                <div className="form-group">
                  <label>Deductions:</label>
                  <input
                    type="number"
                    name="payroll.salary.deductions"
                    value={addForm.payroll.salary.deductions}
                    onChange={handleAddFormChange}
                    className="form-control"
                    placeholder="Deductions"
                    min="0"
                  />
                </div>

                <div className="form-group">
                  <label>Currency:</label>
                  <select
                    name="payroll.salary.currency"
                    value={addForm.payroll.salary.currency}
                    onChange={handleAddFormChange}
                    className="form-control"
                  >
                    {currencyOptions.map(currency => (
                      <option key={currency} value={currency}>{currency}</option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label>Net Salary (Auto-calculated):</label>
                  <input
                    type="number"
                    name="payroll.salary.netSalary"
                    value={
                      (parseFloat(addForm.payroll.salary.basic) || 0) +
                      (parseFloat(addForm.payroll.salary.hra) || 0) +
                      (parseFloat(addForm.payroll.salary.allowances) || 0) -
                      (parseFloat(addForm.payroll.salary.deductions) || 0)
                    }
                    className="form-control"
                    readOnly
                    style={{ backgroundColor: '#f5f5f5' }}
                  />
                </div>


                <h5 style={{ marginTop: '20px', marginBottom: '10px', color: '#555' }}>Bank Details</h5>

                <div className="form-group">
                  <label>Account Holder Name:</label>
                  <input
                    type="text"
                    name="payroll.bankDetails.accountHolderName"
                    value={addForm.payroll.bankDetails.accountHolderName}
                    onChange={handleAddFormChange}
                    className="form-control"
                    placeholder="Account Holder Name"
                  />
                </div>

                <div className="form-group">
                  <label>Account Number:</label>
                  <input
                    type="text"
                    name="payroll.bankDetails.accountNumber"
                    value={addForm.payroll.bankDetails.accountNumber}
                    onChange={handleAddFormChange}
                    className="form-control"
                    placeholder="Account Number (9-18 digits)"
                  />
                  <small className="form-text">Enter 9-18 digit account number</small>
                </div>

                <div className="form-group">
                  <label>IFSC Code:</label>
                  <input
                    type="text"
                    name="payroll.bankDetails.ifscCode"
                    value={addForm.payroll.bankDetails.ifscCode}
                    onChange={handleAddFormChange}
                    className="form-control"
                    placeholder="IFSC Code (e.g., SBIN0001234)"
                  />
                  <small className="form-text">Format: 4 letters + 0 + 6 alphanumeric</small>
                </div>

                <div className="form-group">
                  <label>Bank Name:</label>
                  <input
                    type="text"
                    name="payroll.bankDetails.bankName"
                    value={addForm.payroll.bankDetails.bankName}
                    onChange={handleAddFormChange}
                    className="form-control"
                    placeholder="Bank Name"
                  />
                </div>

                <div className="form-group">
                  <label>Branch:</label>
                  <input
                    type="text"
                    name="payroll.bankDetails.branch"
                    value={addForm.payroll.bankDetails.branch}
                    onChange={handleAddFormChange}
                    className="form-control"
                    placeholder="Branch"
                  />
                </div>


                <h5 style={{ marginTop: '20px', marginBottom: '10px', color: '#555' }}>Tax Details</h5>

                <div className="form-group">
                  <label>PAN Number:</label>
                  <input
                    type="text"
                    name="payroll.taxDetails.panNumber"
                    value={addForm.payroll.taxDetails.panNumber}
                    onChange={handleAddFormChange}
                    className="form-control"
                    placeholder="PAN Number (e.g., ABCDE1234F)"
                    style={{ textTransform: 'uppercase' }}
                  />
                  <small className="form-text">Format: 5 letters + 4 digits + 1 letter</small>
                </div>

                <div className="form-group">
                  <label>PF Number:</label>
                  <input
                    type="text"
                    name="payroll.taxDetails.pfNumber"
                    value={addForm.payroll.taxDetails.pfNumber}
                    onChange={handleAddFormChange}
                    className="form-control"
                    placeholder="PF Number (e.g., AB12345)"
                    style={{ textTransform: 'uppercase' }}
                  />
                  <small className="form-text">Format: 2 letters + 4-7 digits</small>
                </div>

                <div className="form-group">
                  <label>ESI Number:</label>
                  <input
                    type="text"
                    name="payroll.taxDetails.esiNumber"
                    value={addForm.payroll.taxDetails.esiNumber}
                    onChange={handleAddFormChange}
                    className="form-control"
                    placeholder="ESI Number (17 digits)"
                  />
                  <small className="form-text">Enter 17 digit ESI number</small>
                </div>


                <h5 style={{ marginTop: '20px', marginBottom: '10px', color: '#555' }}>Payment Settings</h5>

                <div className="form-group">
                  <label>Payment Mode:</label>
                  <select
                    name="payroll.paymentMode"
                    value={addForm.payroll.paymentMode}
                    onChange={handleAddFormChange}
                    className="form-control"
                  >
                    {paymentModeOptions.map(mode => (
                      <option key={mode} value={mode}>{mode.replace('_', ' ').toUpperCase()}</option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label>Payment Cycle:</label>
                  <select
                    name="payroll.paymentCycle"
                    value={addForm.payroll.paymentCycle}
                    onChange={handleAddFormChange}
                    className="form-control"
                  >
                    {paymentCycleOptions.map(cycle => (
                      <option key={cycle} value={cycle}>{cycle.toUpperCase()}</option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label>Remarks:</label>
                  <textarea
                    name="payroll.remarks"
                    value={addForm.payroll.remarks}
                    onChange={handleAddFormChange}
                    className="form-control"
                    placeholder="Additional remarks"
                    rows="3"
                  />
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="action-btn cancel" onClick={handleCloseAddModal} disabled={isAddingEmployee}>
                Cancel
              </button>
              <button 
                className="action-btn save" 
                onClick={handleAddEmployee}
                disabled={isAddingEmployee}
              >
                {isAddingEmployee ? 'Creating...' : 'Create User'}
              </button>
            </div>
          </div>
        </div>
      )}


      {showPasswordModal && (
        <div className="modal-overlay" onClick={handleClosePasswordModal}>
          <div className="modal-content1" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Update Password</h3>
              <button className="modal-close-btn" onClick={handleClosePasswordModal}>
                ×
              </button>
            </div>
            <div className="modal-body">
              <div className="edit-form">
                <div className="form-group">
                  <label>New Password:*</label>
                  <input
                    type="password"
                    name="newPassword"
                    value={passwordForm.newPassword || ''}
                    onChange={handlePasswordFormChange}
                    className="form-control"
                    placeholder="New Password (min. 6 characters)"
                    required
                  />
                  <small className="form-text">Password must be at least 6 characters long</small>
                </div>
                <div className="form-group">
                  <label>Confirm New Password:*</label>
                  <input
                    type="password"
                    name="confirmPassword"
                    value={passwordForm.confirmPassword || ''}
                    onChange={handlePasswordFormChange}
                    className="form-control"
                    placeholder="Confirm New Password"
                    required
                  />
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="action-btn cancel" onClick={handleClosePasswordModal} disabled={isUpdatingPassword}>
                Cancel
              </button>
              <button 
                className="action-btn save" 
                onClick={handleUpdatePassword}
                disabled={isUpdatingPassword}
              >
                {isUpdatingPassword ? 'Updating...' : 'Update Password'}
              </button>
            </div>
          </div>
        </div>
      )}


      {showDeleteConfirm && (
        <div className="modal-overlay" onClick={() => setShowDeleteConfirm(false)}>
          <div className="modal-content1 confirm-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{deleteType === 'permanent' ? 'Permanently Delete User' : 'Deactivate User'}</h3>
              <button className="modal-close-btn" onClick={() => setShowDeleteConfirm(false)}>
                ×
              </button>
            </div>
            <div className="modal-body">
              <p>
                {deleteType === 'permanent' 
                  ? 'Are you sure you want to permanently delete this user? This action cannot be undone.'
                  : 'Are you sure you want to deactivate this user? They will no longer be able to access the system.'}
              </p>
            </div>
            <div className="modal-footer">
              <button className="action-btn cancel" onClick={() => setShowDeleteConfirm(false)}>
                Cancel
              </button>
              <button 
                className={`action-btn ${deleteType === 'permanent' ? 'permanent-delete' : 'delete'}`} 
                onClick={handleDeleteEmployee}
              >
                {deleteType === 'permanent' ? 'Permanently Delete' : 'Deactivate'}
              </button>
            </div>
          </div>
        </div>
      )}


      {showBulkModal && (
        <div className="modal-overlay" onClick={() => setShowBulkModal(false)}>
          <div className="modal-content1 bulk-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Bulk Create Users</h3>
              <button className="modal-close-btn" onClick={() => setShowBulkModal(false)}>
                ×
              </button>
            </div>
            <div className="modal-body">
              <p>Enter JSON array of users (employees & executives):</p>
              <textarea
                value={bulkEmployees}
                onChange={(e) => setBulkEmployees(e.target.value)}
                className="bulk-textarea"
                rows={10}
                placeholder='[
  {
    "name": "John Doe",
    "email": "john@example.com",
    "password": "password123",
    "type": "employee",
    "role": "employee",
    "phoneNumber": "+1234567890",
    "department": "Operations",
    "position": "Operations Manager",
    "payroll": {
      "employeeId": "EMP001",
      "designation": "Manager",
      "dateOfJoining": "2023-01-01",
      "salary": {
        "basic": 50000,
        "hra": 15000,
        "allowances": 5000,
        "deductions": 3000,
        "currency": "INR"
      },
      "bankDetails": {
        "accountHolderName": "John Doe",
        "accountNumber": "1234567890",
        "ifscCode": "SBIN0001234",
        "bankName": "SBI",
        "branch": "Main Branch"
      },
      "taxDetails": {
        "panNumber": "ABCDE1234F",
        "pfNumber": "AB12345",
        "esiNumber": "12345678901234567"
      },
      "paymentMode": "bank_transfer",
      "paymentCycle": "monthly",
      "status": "active",
      "remarks": "Full-time employee"
    }
  }
]'
              />
            </div>
            <div className="modal-footer">
              <button className="action-btn cancel" onClick={() => setShowBulkModal(false)}>
                Cancel
              </button>
              <button className="action-btn save" onClick={handleBulkCreate}>
                Create Users
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmployeeManagement;